import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, TrendingUp, Shield, Lock, Eye, Server, Zap, CheckCircle, Database, FileText, AlertTriangle } from 'lucide-react';
import { updatePageSEO } from '../utils/seo';

const PrivacyFirstPortfolioTrackerPage = () => {
  const navigate = useNavigate();

  // Update SEO tags when component mounts
  useEffect(() => {
    updatePageSEO({
      title: 'Privacy-First Portfolio Tracker - PlanLens.ai | No Account Linking Required',
      description: 'Track your investments privately with PlanLens.ai. No bank account linking, no data sharing, no third-party access. Self-directed portfolio analysis and wealth management.',
      canonical: 'https://planlens.ai/privacy-first-portfolio-tracker'
    });
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Header */}
      <nav className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold text-gray-900">PlanLens AI</span>
            </div>
            <button
              onClick={() => navigate('/')}
              className="flex items-center space-x-2 text-gray-700 hover:text-blue-600 font-medium transition"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back to Home</span>
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center max-w-4xl mx-auto mb-16">
          <div className="inline-flex items-center space-x-2 bg-green-100 text-green-700 px-4 py-2 rounded-full mb-6">
            <Shield className="w-4 h-4" />
            <span className="text-sm font-semibold">Privacy-First Financial Planning</span>
          </div>

          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            Privacy-First
            <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent"> Portfolio Tracker</span>
          </h1>

          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
            Track your investments with complete privacy. No data sharing, no third-party access, no compromises.
            Your financial data stays yours—always.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4">
            <button
              onClick={() => navigate('/')}
              className="w-full sm:w-auto px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition shadow-lg hover:shadow-xl"
            >
              Start Tracking Privately
            </button>
          </div>
        </div>

        {/* Why Privacy Matters Section */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">Why Privacy Matters in Portfolio Tracking</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="bg-red-50 rounded-xl p-6">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Traditional Portfolio Trackers</h3>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className="flex items-start">
                      <span className="text-red-600 mr-2">×</span>
                      <span>Require linking bank accounts with third-party services</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-red-600 mr-2">×</span>
                      <span>Share your financial data with aggregators</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-red-600 mr-2">×</span>
                      <span>Sell anonymized data to advertisers</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-red-600 mr-2">×</span>
                      <span>Store credentials in third-party databases</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-green-50 rounded-xl p-6">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Shield className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">PlanLens Privacy-First Approach</h3>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className="flex items-start">
                      <span className="text-green-600 mr-2">✓</span>
                      <span>Manual entry—no bank linking required</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-600 mr-2">✓</span>
                      <span>Your data encrypted and stored securely</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-600 mr-2">✓</span>
                      <span>Zero data sharing or selling</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-600 mr-2">✓</span>
                      <span>Complete ownership of your financial information</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 rounded-xl p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
              <Lock className="w-6 h-6 text-blue-600 mr-2" />
              The Privacy Problem with "Free" Financial Apps
            </h3>
            <p className="text-gray-700 mb-4">
              Many "free" portfolio trackers make money by monetizing your data. They track your spending habits,
              investment patterns, and financial behavior, then sell this information to advertisers, data brokers,
              and financial institutions.
            </p>
            <p className="text-gray-700 font-semibold">
              With PlanLens, you're not the product—your privacy is the priority.
            </p>
          </div>
        </div>

        {/* Privacy Features Section */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Privacy-First Features</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <Database className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">No Bank Linking</h3>
              <p className="text-gray-600">
                Manually enter your investment values. Never share bank credentials or give third-party
                services access to your accounts.
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                <Lock className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Encrypted Storage</h3>
              <p className="text-gray-600">
                All your portfolio data is encrypted at rest and in transit using industry-standard
                encryption protocols. Your information stays secure.
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                <Eye className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Zero Data Sharing</h3>
              <p className="text-gray-600">
                We never sell, share, or monetize your data. Your financial information is yours alone—
                not used for advertising or third-party purposes.
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition">
              <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center mb-4">
                <Server className="w-6 h-6 text-amber-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Secure Infrastructure</h3>
              <p className="text-gray-600">
                Hosted on enterprise-grade cloud infrastructure with automatic security updates,
                DDoS protection, and 24/7 monitoring.
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                <FileText className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Data Export Anytime</h3>
              <p className="text-gray-600">
                Export your portfolio data anytime in CSV or PDF format. You maintain complete control
                and can leave whenever you want.
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                <Zap className="w-6 h-6 text-teal-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">GDPR Compliant</h3>
              <p className="text-gray-600">
                Fully compliant with GDPR, CCPA, and international privacy regulations. Your rights
                are protected by law and by design.
              </p>
            </div>
          </div>
        </div>

        {/* How It Works Section */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">How Privacy-First Tracking Works</h2>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-blue-600">1</span>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Create Account</h3>
              <p className="text-sm text-gray-600">
                Sign up with just your email. No personal details required beyond authentication.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-purple-600">2</span>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Add Accounts</h3>
              <p className="text-sm text-gray-600">
                Manually add your investment accounts—401(k), IRA, ISA, SIPP, etc. No linking needed.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-green-600">3</span>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Enter Positions</h3>
              <p className="text-sm text-gray-600">
                Enter your holdings across stocks, bonds, real estate, crypto, and more. Update as needed.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-amber-600">4</span>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Track Privately</h3>
              <p className="text-sm text-gray-600">
                View dashboards, run analyses, and plan retirement—all while keeping your data private.
              </p>
            </div>
          </div>
        </div>

        {/* Full Features Section */}
        <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-8 mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">
            Privacy + Powerful Features
          </h2>
          <p className="text-center text-gray-600 mb-8 max-w-2xl mx-auto">
            You don't have to sacrifice functionality for privacy. PlanLens gives you both.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              'Multi-account portfolio tracking (US & UK)',
              'Net worth dashboard with health scoring',
              'Financial goals with progress tracking',
              'Debt & liability management',
              'AI-powered retirement insights',
              'Monte Carlo simulations',
              'Historical backtesting',
              'Asset allocation analysis',
              'Risk analysis & metrics',
              'Multi-currency support (USD/GBP)',
              'CSV & PDF export',
              'Mobile-responsive design'
            ].map((feature, index) => (
              <div key={index} className="flex items-start space-x-3 bg-white rounded-lg p-4">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">{feature}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Comparison Section */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">PlanLens vs Traditional Trackers</h2>

          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-bold text-gray-900">Feature</th>
                    <th className="px-6 py-4 text-center text-sm font-bold text-green-600">PlanLens</th>
                    <th className="px-6 py-4 text-center text-sm font-bold text-gray-500">Traditional Trackers</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td className="px-6 py-4 text-sm text-gray-900">Bank Account Linking</td>
                    <td className="px-6 py-4 text-center text-green-600 font-semibold">Not Required</td>
                    <td className="px-6 py-4 text-center text-red-600 font-semibold">Required</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-900">Data Sharing</td>
                    <td className="px-6 py-4 text-center text-green-600 font-semibold">Zero</td>
                    <td className="px-6 py-4 text-center text-red-600 font-semibold">Yes (aggregators)</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 text-sm text-gray-900">Data Monetization</td>
                    <td className="px-6 py-4 text-center text-green-600 font-semibold">Never</td>
                    <td className="px-6 py-4 text-center text-red-600 font-semibold">Often</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-900">Encryption</td>
                    <td className="px-6 py-4 text-center text-green-600 font-semibold">End-to-End</td>
                    <td className="px-6 py-4 text-center text-yellow-600 font-semibold">Varies</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 text-sm text-gray-900">Data Ownership</td>
                    <td className="px-6 py-4 text-center text-green-600 font-semibold">You Own It</td>
                    <td className="px-6 py-4 text-center text-red-600 font-semibold">Shared Rights</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-900">Third-Party Access</td>
                    <td className="px-6 py-4 text-center text-green-600 font-semibold">None</td>
                    <td className="px-6 py-4 text-center text-red-600 font-semibold">Multiple Partners</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-12 text-center text-white">
          <h2 className="text-4xl font-bold mb-4">
            Your Privacy. Your Portfolio. Your Control.
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Start tracking your investments with complete privacy today
          </p>
          <button
            onClick={() => navigate('/')}
            className="px-12 py-4 bg-white text-blue-600 rounded-xl font-semibold hover:bg-gray-100 transition shadow-xl hover:shadow-2xl text-lg"
          >
            Get Started Free
          </button>
          <p className="text-sm mt-4 opacity-75">No credit card required • No bank linking • Cancel anytime</p>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm text-gray-400">
            © 2025 PlanLens. Your privacy is our priority.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyFirstPortfolioTrackerPage;
